import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { format } from 'date-fns';
import { Shield, Edit2, Users, CheckSquare, ArrowLeft, Calendar, AlertTriangle } from 'lucide-react';
import { AppShell } from '../../components/layout/AppShell';
import { PageLayout } from '../../components/common/PageLayout';
import { ContentCard } from '../../components/common/ContentCard';
import { Button } from '../../components/common/Button';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { PAGE_COLORS } from '../../utils/pageColors';
import type { RoleDetailsPageProps } from '../../types';
import {
  useGetRoleQuery,
  useGetRolePermissionsQuery,
  useGetRoleUsersQuery,
} from '../../store/slices/apiSlice';
import {
  PAGE_PATH_VIEW_ALTERNATIVES,
  getContractPermissionCodes,
  getPermissionCodeToPagePaths,
} from '../../navigation/permissionPageMap';
import { NAV_TOP_ITEMS, ROLE_PAGE_GROUPS } from '../../navigation/navCatalog';

export const RoleDetailsPage: React.FC<RoleDetailsPageProps> = () => {
  const { id } = useParams<{ id: string }>();

  const {
    data: roleData,
    isLoading: isLoadingRole,
    isError: isRoleError,
  } = useGetRoleQuery(id!);

  const {
    data: permissionsData,
    isLoading: isLoadingPermissions,
  } = useGetRolePermissionsQuery(id!);

  const {
    data: usersData,
    isLoading: isLoadingUsers,
  } = useGetRoleUsersQuery(id!);

  const isLoading = isLoadingRole || isLoadingPermissions || isLoadingUsers;

  if (isLoading) {
    return (
      <AppShell>
        <PageLayout
          title="Role Details"
          subtitle="Loading role information..."
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<Shield className="w-6 h-6" />}
        >
          <ContentCard>
            <div className="flex justify-center py-12">
              <LoadingSpinner size="lg" />
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  if (isRoleError || !roleData) {
    return (
      <AppShell>
        <PageLayout
          title="Role Not Found"
          subtitle="The requested role could not be found"
          bgColor={PAGE_COLORS.settings.bgColor}
          textColor={PAGE_COLORS.settings.textColor}
          borderColor={PAGE_COLORS.settings.borderColor}
          icon={<Shield className="w-6 h-6" />}
        >
          <ContentCard>
            <div className="text-center py-12">
              <p className="text-gray-600 mb-4">The role you're looking for doesn't exist or has been deleted.</p>
              <Link to="/roles">
                <Button icon={<ArrowLeft className="w-4 h-4" />}>
                  Back to Roles
                </Button>
              </Link>
            </div>
          </ContentCard>
        </PageLayout>
      </AppShell>
    );
  }

  const groupPermissionsByCategory = () => {
    if (!permissionsData?.permissions) return {};
    
    return permissionsData.permissions.reduce((acc, item) => {
      const category = item.permission.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item.permission);
      return acc;
    }, {} as Record<string, typeof permissionsData.permissions[0]['permission'][]>);
  };

  const permissionsByCategory = groupPermissionsByCategory();
  const selectedPermissionCodes = new Set(roleData.permissions.map((p) => p.code));
  const contractPermissionCodes = getContractPermissionCodes();
  const codeToPagePaths = getPermissionCodeToPagePaths();

  const pageSelectionGroups = [{ label: 'Main', items: NAV_TOP_ITEMS }, ...ROLE_PAGE_GROUPS];
  const pageLabelByPath = new Map<string, string>();
  pageSelectionGroups.forEach((g) => {
    g.items.forEach((i) => pageLabelByPath.set(i.path, i.label));
  });

  const visiblePathSet = new Set(roleData.visible_page_paths ?? []);
  const orderedVisiblePages: Array<{ path: string; label: string }> = [];
  pageSelectionGroups.forEach((g) => {
    g.items.forEach((i) => {
      if (visiblePathSet.has(i.path)) {
        orderedVisiblePages.push({ path: i.path, label: i.label });
      }
    });
  });
  const catalogPathSet = new Set(orderedVisiblePages.map((r) => r.path));
  (roleData.visible_page_paths ?? []).forEach((p) => {
    if (!catalogPathSet.has(p)) {
      orderedVisiblePages.push({ path: p, label: pageLabelByPath.get(p) ?? p });
    }
  });

  const pagePermissionGaps = (roleData.visible_page_paths ?? [])
    .map((path) => {
      const requiredCodes = PAGE_PATH_VIEW_ALTERNATIVES[path];
      if (!requiredCodes || requiredCodes.length === 0) return null;
      const hasAny = requiredCodes.some((code) => selectedPermissionCodes.has(code));
      if (hasAny) return null;
      return { path, requiredCodes };
    })
    .filter(Boolean) as Array<{ path: string; requiredCodes: readonly string[] }>;

  const orphanedAssignedContractPermissions = roleData.permissions
    .filter((permission) => {
      if (!contractPermissionCodes.has(permission.code)) return false;
      const mappedPaths = codeToPagePaths.get(permission.code);
      if (!mappedPaths || mappedPaths.length === 0) return false;
      return !mappedPaths.some((path) => visiblePathSet.has(path));
    })
    .sort((a, b) => a.name.localeCompare(b.name));

  const apiLegacyCategoryBlocks = Object.entries(permissionsByCategory)
    .map(([category, permissions]) => ({
      category,
      permissions: permissions.filter((p) => !contractPermissionCodes.has(p.code)),
    }))
    .filter((block) => block.permissions.length > 0);

  return (
    <AppShell>
      <PageLayout
        title={roleData.name}
        subtitle="Role details and permissions"
        bgColor={PAGE_COLORS.settings.bgColor}
        textColor={PAGE_COLORS.settings.textColor}
        borderColor={PAGE_COLORS.settings.borderColor}
        icon={<Shield className="w-6 h-6" />}
      >
        <div className="space-y-6">
          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Link to="/roles">
              <Button variant="ghost" icon={<ArrowLeft className="w-4 h-4" />}>
                Back to Roles
              </Button>
            </Link>
            
            <Link to={`/roles/${roleData.id}/edit`}>
              <Button icon={<Edit2 className="w-4 h-4" />}>
                Edit Role
              </Button>
            </Link>
          </div>

          {/* Role Information */}
          <ContentCard title="Role Information">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {roleData.name}
                </h3>
                <p className="text-gray-600 mb-4">
                  {roleData.description}
                </p>
                
                <div className="space-y-2">
                  <div className="flex items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      roleData.is_active
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {roleData.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  
                  <div className="flex items-center text-sm text-gray-600">
                    <Users className="w-4 h-4 mr-2" />
                    {roleData.user_count} users assigned
                  </div>
                </div>
              </div>
              
              <div>
                <h4 className="text-sm font-medium text-gray-900 mb-3">Metadata</h4>
                <div className="space-y-2 text-sm text-gray-600">
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Created: {format(new Date(roleData.created_at), 'MMM d, yyyy')}
                  </div>
                  
                  {roleData.created_by_username && (
                    <div>
                      Created by: {roleData.created_by_username}
                    </div>
                  )}
                  
                  <div className="flex items-center">
                    <Calendar className="w-4 h-4 mr-2" />
                    Updated: {format(new Date(roleData.updated_at), 'MMM d, yyyy')}
                  </div>
                </div>
              </div>
            </div>
          </ContentCard>

          <ContentCard
            title="Pages access"
            subtitle={`${roleData.visible_page_paths?.length ?? 0} page(s) · sidebar order · contract-mapped permissions`}
          >
            {pagePermissionGaps.length > 0 && (
              <div className="mb-4 rounded-md border border-amber-300 bg-amber-50 p-3 text-amber-900">
                <div className="flex items-center gap-2 font-medium">
                  <AlertTriangle className="h-4 w-4" />
                  This role has page / permission mismatches
                </div>
                <ul className="mt-2 space-y-2 text-sm">
                  {pagePermissionGaps.map((gap) => (
                    <li key={gap.path}>
                      <span className="font-semibold">
                        {pageLabelByPath.get(gap.path) ?? gap.path}
                      </span>{' '}
                      <span className="font-mono text-xs">({gap.path})</span>
                      <div className="text-xs">Requires one of: {gap.requiredCodes.join(', ')}</div>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {orderedVisiblePages.length > 0 ? (
              <div className="space-y-3">
                {orderedVisiblePages.map(({ path, label }) => {
                  const requiredCodes = PAGE_PATH_VIEW_ALTERNATIVES[path] ?? [];
                  const grantedForPage = roleData.permissions.filter(
                    (p) =>
                      contractPermissionCodes.has(p.code) && requiredCodes.includes(p.code)
                  );
                  const pageGap =
                    requiredCodes.length > 0 &&
                    !requiredCodes.some((code) => selectedPermissionCodes.has(code));

                  return (
                    <div
                      key={path}
                      className={`rounded-lg border p-4 ${
                        pageGap ? 'border-amber-200 bg-amber-50/40' : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="flex flex-wrap items-baseline justify-between gap-2">
                        <div>
                          <div className="text-sm font-semibold text-gray-900">{label}</div>
                          <div className="font-mono text-xs text-gray-500">{path}</div>
                        </div>
                        {pageGap && (
                          <div className="flex items-center gap-1 text-xs font-medium text-amber-800">
                            <AlertTriangle className="h-3.5 w-3.5" />
                            Missing matching permission
                          </div>
                        )}
                      </div>
                      {requiredCodes.length === 0 ? (
                        <p className="mt-2 text-xs text-gray-500">No contract mapping for this path.</p>
                      ) : grantedForPage.length > 0 ? (
                        <ul className="mt-3 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
                          {grantedForPage.map((p) => (
                            <li
                              key={p.id}
                              className="flex items-start gap-2 rounded-md border border-green-200 bg-green-50 px-2 py-1.5 text-sm"
                            >
                              <CheckSquare className="mt-0.5 h-4 w-4 shrink-0 text-green-600" />
                              <span className="min-w-0">
                                <span className="font-medium text-gray-900">{p.name}</span>
                                <span className="ml-1 font-mono text-xs text-gray-500">({p.code})</span>
                              </span>
                            </li>
                          ))}
                        </ul>
                      ) : (
                        <p className="mt-2 text-xs text-gray-600">
                          No mapped permissions assigned yet. Needs one of:{' '}
                          <span className="font-mono">{requiredCodes.join(', ')}</span>
                        </p>
                      )}
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-gray-500">No page paths configured for this role.</p>
            )}
          </ContentCard>

          <ContentCard
            title="Additional API / legacy permissions"
            subtitle="Assigned permissions not tied to a sidebar page in the access map"
          >
            {orphanedAssignedContractPermissions.length > 0 && (
              <div className="mb-4 rounded-md border border-slate-200 bg-slate-50 p-3 text-slate-800">
                <div className="text-sm font-medium">Assigned permissions with no enabled page</div>
                <p className="mt-1 text-xs text-slate-600">
                  These permissions map to sidebar pages, but those pages are currently turned off
                  for this role.
                </p>
                <ul className="mt-3 grid grid-cols-1 gap-2 md:grid-cols-2">
                  {orphanedAssignedContractPermissions.map((permission) => {
                    const mappedPaths = codeToPagePaths.get(permission.code) ?? [];
                    const mappedPageLabels = mappedPaths.map(
                      (path) => pageLabelByPath.get(path) ?? path
                    );
                    return (
                      <li
                        key={`orphaned-${permission.id}`}
                        className="rounded border border-slate-200 bg-white px-3 py-2"
                      >
                        <div className="text-sm font-medium text-gray-900">{permission.name}</div>
                        <div className="font-mono text-xs text-gray-500">{permission.code}</div>
                        <div className="mt-1 text-xs text-slate-600">
                          Mapped pages: {mappedPageLabels.join(', ')}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
            {apiLegacyCategoryBlocks.length > 0 ? (
              <div className="space-y-6">
                {apiLegacyCategoryBlocks.map(({ category, permissions }) => (
                  <div key={category} className="rounded-lg border border-gray-200 p-4">
                    <h4 className="mb-3 text-lg font-medium capitalize text-gray-900">
                      {category.replace('_', ' ')} ({permissions.length})
                    </h4>
                    <div className="grid grid-cols-1 gap-3 md:grid-cols-2 lg:grid-cols-3">
                      {permissions.map((permission) => (
                        <div
                          key={permission.id}
                          className="flex items-start rounded-md border border-amber-100 bg-amber-50/60 p-3"
                        >
                          <CheckSquare className="mt-0.5 mr-3 h-4 w-4 shrink-0 text-amber-700" />
                          <div className="min-w-0">
                            <div className="text-sm font-medium text-gray-900">{permission.name}</div>
                            <div className="text-xs text-gray-500">{permission.code}</div>
                            <div className="mt-1 text-[11px] font-medium text-amber-800">
                              API-only / legacy
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-gray-500">
                No API-only permissions assigned (all assigned permissions map to sidebar pages).
              </p>
            )}
          </ContentCard>

          {/* Assigned Users */}
          <ContentCard
            title="Assigned Users"
            subtitle={`${usersData?.total_users || 0} users with this role`}
          >
            {usersData && usersData.users.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        User
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {usersData.users.map((user) => (
                      <tr key={user.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div>
                            <div className="text-sm font-medium text-gray-900">
                              {user.full_name || `${user.user_details.first_name} ${user.user_details.last_name}`.trim()}
                            </div>
                            <div className="text-sm text-gray-500">
                              {user.user_details.email}
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            user.status === 'active'
                              ? 'bg-green-100 text-green-800'
                              : user.status === 'inactive'
                              ? 'bg-gray-100 text-gray-800'
                              : 'bg-red-100 text-red-800'
                          }`}>
                            {user.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                          <Link to={`/users/${user.id}`}>
                            <Button variant="ghost" size="sm">
                              View User
                            </Button>
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-8 text-gray-500">
                No users assigned to this role
              </div>
            )}
          </ContentCard>
        </div>
      </PageLayout>
    </AppShell>
  );
};
